import React, { useState } from 'react';
import '../styles/StarRating.css';

/**
 * StarRating Component
 *
 * A flexible star rating component that supports both interactive and read-only modes.
 *
 * @param {number} value - Current rating (0-5)
 * @param {function} onChange - Callback when rating changes (required for interactive mode)
 * @param {boolean} readOnly - If true, stars are display-only (default: false)
 * @param {number} maxStars - Maximum number of stars (default: 5)
 * @param {string} size - Size of stars: 'small' | 'medium' | 'large' (default: 'medium')
 * @param {boolean} showEmpty - Show stars even when rating=0 (default: false)
 */
const StarRating = ({
  value = 0,
  onChange,
  readOnly = false,
  maxStars = 5,
  size = 'medium',
  showEmpty = false
}) => {
  const [hoveredStar, setHoveredStar] = useState(null);

  // Don't render anything if rating is 0 and showEmpty is false
  if (!showEmpty && value === 0 && readOnly) {
    return null;
  }

  const handleClick = (starIndex) => {
    if (readOnly || !onChange) return;

    // If clicking the active star, remove rating (set to 0)
    if (starIndex === value) {
      onChange(0);
    } else {
      // Set rating to clicked star
      onChange(starIndex);
    }
  };

  const handleMouseEnter = (starIndex) => {
    if (!readOnly) {
      setHoveredStar(starIndex);
    }
  };

  const handleMouseLeave = () => {
    if (!readOnly) {
      setHoveredStar(null);
    }
  };

  const getStarIcon = (starIndex) => {
    // Determine if this star should be filled
    const isFilled = hoveredStar !== null
      ? starIndex <= hoveredStar
      : starIndex <= value;

    return isFilled ? '⭐' : '☆';
  };

  const getStarClassName = (starIndex) => {
    const classes = ['star'];

    // Add size class
    classes.push(`star--${size}`);

    // Add state classes
    if (readOnly) {
      classes.push('star--readonly');
    } else {
      classes.push('star--interactive');
    }

    // Add filled/active state
    if (hoveredStar !== null) {
      if (starIndex <= hoveredStar) {
        classes.push('star--hovered');
      }
    } else if (starIndex <= value) {
      classes.push('star--active');
    }

    return classes.join(' ');
  };

  return (
    <div className={`star-rating star-rating--${size} ${readOnly ? 'star-rating--readonly' : 'star-rating--interactive'}`}>
      {Array.from({ length: maxStars }, (_, index) => {
        const starIndex = index + 1;
        return (
          <span
            key={starIndex}
            className={getStarClassName(starIndex)}
            onClick={() => handleClick(starIndex)}
            onMouseEnter={() => handleMouseEnter(starIndex)}
            onMouseLeave={handleMouseLeave}
            role={readOnly ? 'img' : 'button'}
            aria-label={readOnly ? `${value} von ${maxStars} Sternen` : `${starIndex} Stern${starIndex > 1 ? 'e' : ''}`}
            tabIndex={readOnly ? -1 : 0}
            onKeyDown={(e) => {
              if (!readOnly && (e.key === 'Enter' || e.key === ' ')) {
                e.preventDefault();
                handleClick(starIndex);
              }
            }}
          >
            {getStarIcon(starIndex)}
          </span>
        );
      })}
    </div>
  );
};

export default StarRating;
